import React from "react";
import { useThemeMode } from "../../../context/contextTheme";
import { useMalware } from "../../../context/Dashboard/contextMalware";
import { useDesign } from "../../../context/contextDesign";
import hexToRGBA from "../../../utils/hexToRGBA";

const OnLoadFile = () => {
  const { activeTabResult, handleClickResult } = useMalware();
  const { viewHeight } = useDesign();
  const { theme } = useThemeMode();

  const tabButtonStyle = (isActive) => ({
    height: 48,
    cursor: "pointer",
    fontWeight: 700,
    fontSize: "0.85rem",
    letterSpacing: "1px",
    color: isActive ? theme.textColor: theme.textMuted,
    borderBottom: isActive ? `3px solid #344266` : "3px solid transparent",
    transition: "all 0.3s ease",
    background: "transparent",
    borderLeft: 0,
    borderRight: 0,
    borderTop: 0,
    borderRadius: 0,
  });

  return (
    <div className="w-100 px-4 py-3">
      <HeaderView />
      
      <div className="mt-4">
        <div className="d-flex align-items-center mb-3 border-bottom" style={{ borderColor: theme.border }}>
          <button 
            className="px-4" 
            style={tabButtonStyle(activeTabResult === 'Detection')}
            onClick={() => handleClickResult('Detection')}
          >
            DETECTION
          </button>
          <button 
            className="px-4" 
            style={tabButtonStyle(activeTabResult === 'Details')}
            onClick={() => handleClickResult('Details')}
          >
            FILE DETAILS
          </button>
        </div>

        <div className="overflow-auto custom-scrollbar" style={{ height: viewHeight }}>
          {activeTabResult === 'Detection' && <DetectionView />}
          {activeTabResult === 'Details' && <DetailsView />}
        </div>
      </div>
    </div>
  );
};

function HeaderView() {
  const { response } = useMalware();
  const attr = response?.attributes;
  const fileSize = response?.meta?.file_info?.size || 0;
  const stats = attr?.stats || {};
  const scanDate = convertToRelativeTime(attr?.date);

  const StatItem = ({ label, value, color }) => (
    <div className="px-3 text-center">
      <div className="small opacity-75 mb-0">{label}</div>
      <div className="fw-bold fs-5" style={{ color: color || '#fff' }}>{value}</div>
    </div>
  );

  return (
    <div className="card border-0 rounded-4 shadow-sm text-white overflow-hidden" style={{ background: '#344266' }}>
      <div className="card-body p-4">
        <div className="row align-items-center">
          <div className="col-md-5 border-end border-white border-opacity-10">
            <div className="d-flex align-items-center">
              <div className="bg-white bg-opacity-10 p-3 rounded-3 me-3">
                <i className="bi bi-file-earmark-binary fs-3"></i>
              </div>
              <div>
                <h6 className="mb-1 opacity-75">File Analysis Report</h6>
                <div className="fw-bold fs-5 text-truncate" style={{ maxWidth: '250px' }}>
                  Size: {(fileSize / 1024).toFixed(2)} KB
                </div>
                <div className="small opacity-50">Scanned {scanDate}</div>
              </div>
            </div>
          </div>
          <div className="col-md-7 mt-3 mt-md-0">
            <div className="d-flex justify-content-around">
              <StatItem label="Malicious" value={stats.malicious} color="#ff4d4d" />
              <StatItem label="Suspicious" value={stats.suspicious} color="#ffc107" />
              <StatItem label="Harmless" value={stats.harmless} color="#00a73d" />
              <StatItem label="Undetected" value={stats.undetected} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function DetectionView() {
  const { response } = useMalware();
  const { theme } = useThemeMode();
  const results = response?.attributes?.results || {};

  return (
    <div className="card border-0 rounded-4 shadow-sm" style={{ backgroundColor: theme.labelBgMuted }}>
      <div className="table-responsive">
        <table className="table table-hover align-middle mb-0" style={{ color: theme.textColor }}>
          <thead className="small text-muted">
            <tr style={{ borderBottom: `1px solid ${theme.border}` }}>
              <th className="ps-4 py-3">SECURITY ENGINE</th>
              <th className="py-3">METHOD</th>
              <th className="py-3">CATEGORY</th>
              <th className="pe-4 py-3 text-end">RESULT</th>
            </tr>
          </thead>
          <tbody>
            {Object.keys(results).map((key) => {
              const r = results[key];
              const isClean = r.result === 'clean' || r.result === 'undetected';
              const isMalicious = r.result === 'malicious' || r.result === 'phishing';
              
              const getBadgeColor = () => {
                if (isClean) return { bg: hexToRGBA('#00a73d', 0.1), text: '#00a73d', icon: 'shield-check' };
                if (isMalicious) return { bg: hexToRGBA('#dc3545', 0.1), text: '#dc3545', icon: 'bug' };
                return { bg: hexToRGBA(theme.textMuted, 0.1), text: theme.textMuted, icon: 'question-circle' };
              };

              const style = getBadgeColor();

              return (
                <tr key={key} style={{ borderBottom: `1px solid ${hexToRGBA(theme.border, 0.3)}` }}>
                  <td className="ps-4 py-3 fw-bold">{r.engine_name}</td>
                  <td className="py-3 text-muted">{r.method}</td>
                  <td className="py-3 text-muted small">{r.category || 'N/A'}</td>
                  <td className="pe-4 py-3 text-end">
                    <span className="badge rounded-pill px-3 py-2" style={{ backgroundColor: style.bg, color: style.text }}>
                      <i className={`bi bi-${style.icon} me-1`}></i>
                      {(r.result || 'unknown').toUpperCase()}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DetailsView() {
  const { response } = useMalware();
  const { theme } = useThemeMode();
  const attr = response.attributes;

  const InfoCard = ({ title, icon, data }) => (
    <div className="col-lg-4 mb-4">
      <div className="card h-100 border-0 rounded-4 shadow-sm" style={{ backgroundColor: theme.labelBgMuted }}>
        <div className="card-body p-4">
          <div className="d-flex align-items-center mb-3">
            <div className="p-2 rounded-2 me-2" style={{ background: hexToRGBA('#344266', 0.1), color: '#344266' }}>
              <i className={`bi bi-${icon}`}></i>
            </div>
            <h6 className="fw-bold mb-0" style={{ color: theme.textColor }}>{title}</h6>
          </div>
          <div className="small">
            {Object.entries(data).map(([key, val]) => (
              <div key={key} className="mb-2 d-flex flex-column">
                <span className="text-muted text-uppercase" style={{ fontSize: '0.65rem', fontWeight: 700 }}>{key.replace(/_/g, ' ')}</span>
                <span className="text-break fw-medium" style={{ color: theme.textColor }}>{val || 'N/A'}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="row">
      <InfoCard 
        title="File Hashes" 
        icon="hash" 
        data={{
          "SHA-256": attr.sha256,
          "MD5": attr.md5,
          "Magic": attr.magic
        }} 
      />
      <InfoCard 
        title="History" 
        icon="clock-history" 
        data={{
          "First Seen": new Date(attr.first_submission_date * 1000).toLocaleString(),
          "Last Seen": new Date(attr.last_submission_date * 1000).toLocaleString(),
          "Last Scanned": new Date(attr.last_analysis_date * 1000).toLocaleString()
        }} 
      />
      <InfoCard 
        title="Community Votes" 
        icon="people" 
        data={{
          "Harmless Votes": attr.total_votes?.harmless,
          "Malicious Votes": attr.total_votes?.malicious
        }} 
      />
    </div>
  );
}

const convertToRelativeTime = (timestamp) => {
  if (!timestamp) return "N/A";
  const diff = Math.floor(Date.now() / 1000) - timestamp;
  if (diff < 60) return "Just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

export default OnLoadFile;